export const AI_ASSISTANT_CONSTANTS = {
    /** Logo.dev publishable key (pk_) for img.logo.dev CDN; safe for client-side. */
    LOGO_DEV_PUBLISHABLE_KEY: import.meta.env?.VITE_LOGO_DEV_PUBLISHABLE_KEY ?? "",
    SELECTED_VALUES: {
        NEWS_SEARCH: "newssearch",
    },
    FEATURE_FLAGS: {
        AI_ASSISTANT_CHAT_ENABLED: "ai_assistant_chat_enabled",
        AI_ASSISTANT_SUMMARISE_SCREEN_ENABLED: "ai_assistant_summarise_screen_enabled",
        AI_ASSISTANT_REPORT_BUILDER_ENABLED: "ai_assistant_report_builder_enabled",
        AI_ASSISTANT_HISTORY_METADATA_CONTEXT_ENABLED: "ai_assistant_history_metadata_context_enabled",
        AI_ASSISTANT_MULTILINGUAL_ENABLED: "ai_assistant_multilingual_enabled"
    },
    DYNAMIC_CONFIGS: {
        AI_ASSISTANT_CONFIGS: "aiassistantconfigs",
        AUTO_OPEN_NAVIGATION_LINKS: "autoOpenNavigationLinks",
        AUTO_OPEN_AND_HIDE_ASSUMPTIONS: "autoOpenAndHideAssumptions",
        LOU_CONFIGURATIONS: "Lou_configurations"
    },
    EVENTS: {
        SESSION_UPDATED: "assistant:session-updated",
        IDENTITY_UPDATED: "assistant:identity-updated",
        SESSION_RESET: "assistant:session-reset",
        TOAST: "assistant:toast"
    },
    HANDOFF: {
        PREFIX: "ai-assistant-handoff:",
        QUERY_KEY: "ai_source_conversation_id",
        DEFAULT_TARGET: "_blank",
        DEFAULT_FEATURES: "noopener,noreferrer"
    },
    ABLY: {
        AUTH_PATH: "ably-token",
        QUERY_KEYS: {
            CLIENT_ID: "clientId",
            RND: "rnd"
        },
        HEARTBEAT_INTERVAL_MS: 30000,
        // How often to re-check a service that did not answer. Long enough
        // not to hammer an outage, short enough that a user who steps away
        // for a minute comes back to a working assistant.
        REACHABILITY_RETRY_MS: 20000,
        CONNECTION_FAILED_EVENT: "failed"
    },
    STORAGE_KEYS: {
        USER_INFO: "userInfo",
        AUTH_TOKEN: "token",
        AI_ASSISTANT_INTRO_SEEN_PREFIX: "ai_assistant_intro_seen::",
    },
    MESSAGE: {
        TYPES: {
            TEXT: "text",
            LINK: "link",
            ASSUMPTION: "assumption",
            SOURCES: "sources",
            CHART: "chart",
            TABLE: "table",
            REPORT: "report",
            ERROR: "error",
            WARNING: "warning"
        },
        DEFAULT_SENDER: "You",
        DEFAULT_AGENT: "Lou",
        DEFAULT_USER_INITIAL: "U",
        DEFAULT_LINK_TITLE: "Link",
        DEFAULT_ASSUMPTIONS_TITLE: "Assumptions",
        FALLBACK_FIELDS: [
            "text",
            "assumption",
            "link",
            "title",
            "description",
            "sources",
            "chartConfig",
            "tableConfig",
            "report_id",
            "report_data",
            "report_status",
            "report_status_message",
            "report_title",
            "error",
            "warning"
        ],
        MARKDOWN_LINK_TARGET: "_blank",
        MARKDOWN_LINK_REL: "noopener noreferrer"
    },
    CHAT: {
        TITLE: "Lou",
        CLEAR_STATES: {
            IDLE: "idle",
            CLEARING: "clearing",
            FAILED: "failed"
        },
        ACTION_CONTROLLER_TITLE: "Information Sources",
        CITATIONS_LABEL: "Citations",
        TOOLTIPS: {
            NEW_CHAT: "New chat",
            SWITCH_TO_LIGHT: "Switch to light",
            SWITCH_TO_DARK: "Switch to dark",
            CLOSE: "Close"
        },
        ARIA: {
            START_NEW_CHAT: "Start new chat",
            TOGGLE_THEME: "Toggle theme",
            CLOSE: "Close"
        }
    },
    TOASTS: {
        TYPES: {
            SUCCESS: "success",
            ERROR: "error",
            WARNING: "warning",
            INFO: "info"
        },
        TITLES: {
            SUCCESS: "Operation Completed Successfully",
            ERROR: "Action Could Not Complete",
            WARNING: "Assistant Warning Notice",
            INFO: "Assistant Status Update",
            REQUEST_IN_PROGRESS: "Request Already Running",
            RESPONSE_DELAYED: "Response Taking Longer",
            CONNECTION_UNSTABLE: "Realtime Connection Unstable",
            SESSION_INIT_FAILED: "Session Setup Failed"
        },
        DEFAULT_DESCRIPTIONS: {
            SUCCESS: "The action was completed successfully.",
            ERROR: "We could not complete this action. Please try again.",
            WARNING: "Please review this notice before proceeding.",
            INFO: "A new assistant update is available."
        },
        DESCRIPTIONS: {
            REQUEST_IN_PROGRESS:
                "A request is already in progress. Please wait for the current response to finish.",
            RESPONSE_DELAYED:
                "The response is taking longer than expected. We are still processing your request.",
            CONNECTION_UNSTABLE:
                "Realtime updates may be delayed while the connection recovers.",
            SESSION_INIT_FAILED:
                "We couldn't initialize your assistant session for this login. Please refresh and try again."
        },
        DURATIONS_MS: {
            DEFAULT: 2500,
            SHORT: 2000
        },
        CLOSE_ARIA_LABEL: "Close toast"
    },
    STATUS: {
        TASK_STATUS_END: "task_status_end",
        DEFAULT_TASK_MESSAGE: "Analysing the request..."
    },
    NEWS_SEARCH: {
        VALUE: "newssearch",
        FLAGS_KEY: "newsSearchEnabled",
        TOOLTIP_ENABLE: "Enable news search",
        TOOLTIP_DISABLE: "Disable news search",
        TOGGLE_ARIA_LABEL: "Toggle news search"
    },
    SUMMARY: {
        HIDDEN_ROUTES: ["/reports"],
        ACTION_LABEL: "Summary",
        PROMPT: "Summarize this screen"
    },
    REPORT: {
        ACTION_LABEL: "Build Report",
        INITIAL_TITLE: "Building your report",
        INITIAL_DESCRIPTION: "Gathering the freshest insights from this view.",
        INITIAL_STATUS_MESSAGE: "Initializing workspace...",
        BUILD_TITLE: "Building the report",
        STATUS: {
            PROCESSING: "processing",
            SUCCESS: "success",
            FAILURE: "failure"
        },
        STAGES: {
            INITIALIZING: "initializing",
            SECTION_READY: "section_ready",
            COMPLETED: "completed",
            ERROR: "error"
        },
        READY_MESSAGE: "Report ready",
        DEFAULT_TITLE: "AI Report",
        ERROR_FALLBACK: "Report build failed.",
        FAILED_TITLE: "Failed to build report",
        STREAM_END_ERROR: "Report stream ended unexpectedly."
    },
    INPUT: {
        PLACEHOLDER: "Message Lou",
        CONNECTING_PLACEHOLDER: "Connecting Lou...",
        UNAVAILABLE_PLACEHOLDER: "Lou is currently unavailable",
        SEND_ARIA_LABEL: "Send message",
        TYPING_TIMEOUT_MS: 3000,
        MAX_TEXTAREA_HEIGHT: 200
    },
    ACTIONS: {
        NEW_CHAT_TITLE: "New Chat Created",
        NEW_CHAT_DESCRIPTION:
            "Your previous conversation was cleared. You can start a new request now.",
        CLEAR_FAILED_TITLE: "New Chat Failed",
        CLEAR_FAILED_DESCRIPTION:
            "We couldn't start a new chat right now. Please try again."
    },
    DISCLAIMER:
        "Responses are generated by AI and may contain mistakes. Please verify important details.",
    API: {
        DEFAULT_BASE_URL: "https://api.dev.theharrispoll.dev/ai-assistant",
        ENDPOINTS: {
            USER_MESSAGE: "usermessage",
            BUILD_REPORT: "build-report",
            ABLY_TOKEN: "ably-token"
        },
        HEADERS: {
            CLIENT_ID: "Client-Id",
            CONTENT_TYPE: "Content-Type",
            JSON: "application/json",
            AUTHORIZATION: "Authorization"
        }
    },
    ERRORS: {
        INTERNAL_SERVER: "Internal Server error",
        UI_UNEXPECTED: "Unexpected UI Error. Please contact the admin.",
        SESSION_UNAVAILABLE: "Unable to establish assistant session",
        SERVER_ERROR: "Server error",
        REQUEST_TAKING_LONGER:
            "Request is taking longer to respond. Please wait while we process your request.",
        REQUEST_ALREADY_IN_PROGRESS:
            "A new request was initiated before the previous request completed. Please wait for the current response to finish.",
        REALTIME_CONNECTION_UNSTABLE:
            "Realtime connection is unstable. Responses may be delayed.",
        REALTIME_CONNECTION_FAILED:
            "Realtime connection failed. Please retry your request.",
        STREAM_UNSUPPORTED: "ReadableStream not supported",
        CLEAR_CHAT_FAILED: "Clear chat failed",
        AUTH_EXPIRED:
            "Your session expired. Please log in again.",
        ASSISTANT_CONNECTING:
            "Connecting to Lou. Please wait before sending a request.",
        ASSISTANT_RECONNECTING:
            "Reconnecting to Lou. Please wait before sending a request.",
        SESSION_INIT_BLOCKING:
            "Assistant session is not initialized due to service unavailability. Refresh the page and try again.",
        REQUEST_FAILED:
            "The assistant had trouble responding. Please try again.",
        REQUEST_TIMEOUT:
            "The assistant request timed out before completion. Please try again.",
        UPSTREAM_UNAVAILABLE:
            "The assistant service is temporarily unreachable. Please try again.",
        EMPTY_RESPONSE:
            "Lou returned an empty response for this request. Please try again.",
        EMPTY_RESPONSE_POSSIBLE_TIMEOUT:
            "The server returned an empty response for this request. This may be due to a timeout. Please try again."
    }
}
